// ─────────────────────────────────────────────────────────────────────────────
// tap_audio.ts — Per-event audio voicings, routed through the engine
//
// This module no longer owns an AudioContext or a master gain. It's a
// thin instrument layer: each method asks the shared `audioEngine` for
// a channel, builds an oscillator + envelope, and connects to that
// channel's input. The engine handles mixer, effects rack, master, and
// mono enforcement transparently downstream.
//
// Channel routing:
//   instrument  — playTap, playResolve, playCast, playMisfire,
//                 playArcaneResolve, startCharge / stopCharge
//   texture     — startWandTension / updateWandTension / stopWandTension
//                 (plus any future pen-scratching loop)
// ─────────────────────────────────────────────────────────────────────────────

import { audioEngine } from './audio_engine';

// ── Pitch tables ─────────────────────────────────────────────────────────────

// Pentatonic steps used for escalating tap tones (Hz)
const TAP_PITCHES = [329.6, 392.0, 523.3];   // E4, G4, C5

// Resolve chord (Hz) — bright arpeggio burst
const RESOLVE_PITCHES = [523.3, 659.3, 784.0]; // C5, E5, G5

// Family root notes for cast climax
const FAMILY_ROOTS: Record<string, number> = {
  act:       440.0,    // A4
  bind:      392.0,    // G4
  transmute: 466.2,    // A#4
  invoke:    523.3,    // C5
  unknown:   349.2,    // F4
};

// ── TapAudio ─────────────────────────────────────────────────────────────────

export class TapAudio {
  // Charge sweep state (instrument channel — sustained but pitched)
  private _chargeOsc: OscillatorNode | null = null;
  private _chargeGain: GainNode | null = null;

  // ── Tap tones ──────────────────────────────────────────────────────────

  /** Play an escalating tap tone based on position in the cadence. */
  playTap(tapIndex: number, _total: number): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    const freq = TAP_PITCHES[Math.min(tapIndex, TAP_PITCHES.length - 1)];

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const env = ctx.createGain();
    const now = ctx.currentTime;
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(0.18, now + 0.01);
    env.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

    osc.connect(env).connect(ch.input);
    osc.start(now);
    osc.stop(now + 0.2);
  }

  /** Per-tap pulse note: distinguishes the gap that *led* to this tap.
   *  Null on the first tap of a sequence (no preceding gap → soft mid
   *  note). Short pulse → bright high; long pulse → warm low. Pitches
   *  picked to harmonise with playResolve so a chained sequence reads
   *  as a single phrase rather than colliding voices. */
  playTapPulse(pulse: 'S' | 'L' | null): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    const now = ctx.currentTime;

    let freq: number;
    let type: OscillatorType;
    let dur: number;
    let vol: number;
    if (pulse === 'S') {
      freq = 587.3;          // D5 — bright purple
      type = 'triangle';
      dur = 0.14;
      vol = 0.16;
    } else if (pulse === 'L') {
      freq = 329.6;          // E4 — warm blue
      type = 'sine';
      dur = 0.22;
      vol = 0.18;
    } else {
      freq = 440.0;          // A4 — neutral first-tap
      type = 'sine';
      dur = 0.16;
      vol = 0.14;
    }

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(vol, now + 0.008);
    env.gain.exponentialRampToValueAtTime(0.001, now + dur);

    osc.connect(env).connect(ch.input);
    osc.start(now);
    osc.stop(now + dur + 0.05);
  }

  /** Bright accented hit fired at pen-DOWN when the engine predicts
   *  this tap will resolve an invocation. Replaces the per-tap drum
   *  kick + the playResolve chord — a single emphatic note that lands
   *  *with* the user's contact instead of trailing it. Three close
   *  partials stacked within ~25 ms give the hit its signature snap. */
  playInvokeStrike(patternId: string, pressure: number = 0.6): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    const now = ctx.currentTime;

    // Per-invocation root pitch — same family as playResolve so the
    // ear maps the strike to its invocation, but voiced as one
    // accented hit rather than a staggered chord.
    let root: number;
    switch (patternId) {
      case 'thrice-knock':  root = 329.6; break;   // E4
      case 'stylus-summon': root = 493.9; break;   // B4
      case 'analyse-knock': root = 440.0; break;   // A4
      default:              root = 392.0; break;   // G4
    }

    // Pressure boosts loudness (capped) — a firm strike rings a
    // touch louder, but stays inside the mix.
    const vol = 0.30 + Math.max(0, Math.min(1, pressure)) * 0.10;
    const partials = [1, 1.5, 2];   // root, 5th, octave
    const types: OscillatorType[] = ['triangle', 'sine', 'sine'];

    for (let i = 0; i < partials.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = types[i];
      osc.frequency.value = root * partials[i];

      const env = ctx.createGain();
      const onset = now + i * 0.008;        // 8 ms stagger — chord-stab snap
      const peak = vol * (1 - i * 0.15);
      env.gain.setValueAtTime(0, onset);
      env.gain.linearRampToValueAtTime(peak, onset + 0.005);
      env.gain.exponentialRampToValueAtTime(0.001, onset + 0.22);

      osc.connect(env).connect(ch.input);
      osc.start(onset);
      osc.stop(onset + 0.25);
    }
  }

  /** Play a resolve sound when a cadence pattern matches — voiced per invocation. */
  playResolve(patternId: string): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    const now = ctx.currentTime;

    let pitches: number[];
    let type: OscillatorType;
    let stagger: number;
    let dur: number;
    let vol: number;

    switch (patternId) {
      case 'thrice-knock':
        pitches = [220, 277.2, 329.6];
        type = 'sine';
        stagger = 0.03;
        dur = 0.40;
        vol = 0.28;
        break;
      case 'stylus-summon':
        pitches = [392, 493.9, 587.3];
        type = 'triangle';
        stagger = 0.025;
        dur = 0.30;
        vol = 0.32;
        break;
      case 'analyse-knock':
        pitches = [349.2, 440, 523.3];
        type = 'sine';
        stagger = 0.035;
        dur = 0.35;
        vol = 0.26;
        break;
      default:
        pitches = RESOLVE_PITCHES;
        type = 'triangle';
        stagger = 0.04;
        dur = 0.35;
        vol = 0.35;
        break;
    }

    for (let i = 0; i < pitches.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = pitches[i];

      const env = ctx.createGain();
      const onset = now + i * stagger;
      env.gain.setValueAtTime(0, onset);
      env.gain.linearRampToValueAtTime(vol, onset + 0.01);
      env.gain.exponentialRampToValueAtTime(0.001, onset + dur);

      osc.connect(env).connect(ch.input);
      osc.start(onset);
      osc.stop(onset + dur + 0.05);
    }
  }

  // ── Cast-mode scratch bias (replaces the old wand-tension drone) ──────
  //
  // Cast mode used to layer a separate hum oscillator on top of the
  // pen-on-paper noise. The new model is simpler and matches the
  // user's mental picture: paper texture is the baseline for both
  // drawing and casting; cast just biases the scratch tone (lower
  // filter, tighter Q, slightly quieter). One sound path; no extra
  // drone bleeding through.

  /** Engage the cast-mode scratch bias. */
  startWandTension(): void {
    audioEngine.texture()?.setCastMode(true);
  }

  /** Update wand tension. With the hum gone there's nothing to swell —
   *  kept as a no-op so callers wired to it don't need to change. */
  updateWandTension(_progress: number): void {
    /* no-op (kept for API compatibility) */
  }

  /** Drop cast-mode bias and let the texture fall back to its default
   *  scratch character. */
  stopWandTension(): void {
    audioEngine.texture()?.setCastMode(false);
  }

  // ── Charge wavefront sound (instrument channel — pitched sweep) ───────

  /** Rising sweep during the charge wavefront. */
  startCharge(durationMs: number): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    this.stopCharge();

    this._chargeOsc = ctx.createOscillator();
    this._chargeOsc.type = 'sine';
    const now = ctx.currentTime;
    const dur = durationMs / 1000;

    this._chargeOsc.frequency.setValueAtTime(261.6, now);
    this._chargeOsc.frequency.exponentialRampToValueAtTime(523.3, now + dur);

    this._chargeGain = ctx.createGain();
    this._chargeGain.gain.setValueAtTime(0, now);
    this._chargeGain.gain.linearRampToValueAtTime(0.18, now + dur * 0.3);
    this._chargeGain.gain.setValueAtTime(0.18, now + dur * 0.8);
    this._chargeGain.gain.exponentialRampToValueAtTime(0.001, now + dur + 0.05);

    this._chargeOsc.connect(this._chargeGain).connect(ch.input);
    this._chargeOsc.start(now);
    this._chargeOsc.stop(now + dur + 0.1);
  }

  /** Stop charge sound early (e.g. if interrupted). */
  stopCharge(): void {
    if (this._chargeOsc) {
      try { this._chargeOsc.stop(); } catch { /* already stopped */ }
      this._chargeOsc = null;
    }
    this._chargeGain = null;
  }

  // ── Cast climax (instrument) ──────────────────────────────────────────

  /** Bright chord on successful spell execution. */
  playCast(family: string): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    const root = FAMILY_ROOTS[family] ?? FAMILY_ROOTS['unknown'];
    const now = ctx.currentTime;

    const intervals = [1, 1.26, 1.5];
    for (let i = 0; i < intervals.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = i === 0 ? 'triangle' : 'sine';
      osc.frequency.value = root * intervals[i];

      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(0.3 - i * 0.05, now + 0.02);
      env.gain.exponentialRampToValueAtTime(0.001, now + 0.55);

      osc.connect(env).connect(ch.input);
      osc.start(now);
      osc.stop(now + 0.6);
    }
  }

  // ── Misfire (instrument) ──────────────────────────────────────────────

  /** Dull thud on spell failure. */
  playMisfire(): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.15);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.25, now);
    env.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

    osc.connect(env).connect(ch.input);
    osc.start(now);
    osc.stop(now + 0.25);
  }

  // ── Arcane analysis feedback (instrument) ─────────────────────────────

  /** Rising crystalline arpeggio when arcane analysis resolves successfully. */
  playArcaneResolve(): void {
    const ctx = audioEngine.context();
    const ch = audioEngine.channel('instrument');
    if (!ctx || !ch) return;
    const now = ctx.currentTime;

    const freqs = [440, 660, 880];
    for (let i = 0; i < freqs.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freqs[i];

      const env = ctx.createGain();
      const onset = now + i * 0.06;
      env.gain.setValueAtTime(0, onset);
      env.gain.linearRampToValueAtTime(0.18, onset + 0.01);
      env.gain.exponentialRampToValueAtTime(0.001, onset + 0.32);

      osc.connect(env).connect(ch.input);
      osc.start(onset);
      osc.stop(onset + 0.4);
    }
  }
}
